import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Shield, Flame, TrendingUp, Code, Users, Sparkles, ArrowRight, Globe, Eye, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODE_MAP = {
  viral: { label: 'Viral', color: '#EF4444', icon: Flame },
  authority: { label: 'Authority', color: '#8B5CF6', icon: Shield },
  conversion: { label: 'Conversion', color: '#10B981', icon: TrendingUp },
  technical: { label: 'Technical', color: '#3B82F6', icon: Code },
  community: { label: 'Community', color: '#F59E0B', icon: Users },
};

export default function GalleryPage() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [filterMode, setFilterMode] = useState('all');
  const [collective, setCollective] = useState(null);
  const navigate = useNavigate();

  const loadGallery = useCallback(async () => {
    try {
      const [galRes, colRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/collective-intelligence`).catch(() => ({ data: null }))
      ]);
      setStrategies(galRes.data || []);
      if (colRes.data?.intelligence) setCollective(colRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGallery(); }, [loadGallery]);
  useEffect(() => {
    document.body.style.backgroundColor = '#07070A';
    document.body.style.color = '#fff';
    return () => { document.body.style.backgroundColor = ''; document.body.style.color = ''; };
  }, []);

  return (
    <div className="min-h-screen font-landing relative" style={{ background: '#07070A' }} data-testid="gallery-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="bg-grid-animated absolute inset-0" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(124,92,255,0.06) 0%, transparent 70%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 pt-8 pb-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/')} className="text-zinc-500 hover:text-white transition-colors" data-testid="gallery-back"><ArrowLeft className="w-4 h-4" /></button>
          <div className="flex-1" />
          <button onClick={() => navigate('/')} className="text-[10px] font-mono text-[#7C5CFF] hover:text-white transition-colors flex items-center gap-1"><Globe className="w-3 h-3" />Analyze yours</button>
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] px-3 py-1.5 rounded-full mb-4" style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.12)' }}>
            <Sparkles className="w-3 h-3" />Live Strategies
          </div>
          <h1 className="text-3xl font-bold text-gradient-purple mb-2">Winning Moves Today</h1>
          <p className="text-sm text-zinc-500">Real strategies generated for real products. See what's working.</p>
        </div>

        {/* Filter bar */}
        <div className="flex items-center justify-center gap-1.5 mb-6" data-testid="gallery-filters">
          {['all', 'authority', 'technical', 'conversion', 'viral'].map(mode => {
            const m = MODE_MAP[mode] || { label: 'All', color: '#7C5CFF' };
            const active = filterMode === mode;
            return (
              <button key={mode} data-testid={`filter-${mode}`} onClick={() => setFilterMode(mode)}
                className={`px-3 py-1 rounded-lg text-[10px] font-mono uppercase transition-all ${active ? 'text-white' : 'text-zinc-600 hover:text-zinc-400'}`}
                style={active ? { background: `${m.color || '#7C5CFF'}12`, border: `1px solid ${m.color || '#7C5CFF'}25` } : { border: '1px solid transparent' }}>
                {mode === 'all' ? 'All' : m.label}
              </button>
            );
          })}
        </div>

        {/* Network leaderboard */}
        {collective?.intelligence?.leaderboard?.length > 0 && (
          <div className="max-w-2xl mx-auto mb-8 rounded-xl px-4 py-3" data-testid="gallery-leaderboard"
            style={{ background: '#0F0F14', border: '1px solid rgba(124,92,255,0.1)' }}>
            <p className="text-[9px] font-mono uppercase tracking-wider text-[#7C5CFF] mb-2">Strategy Leaderboard</p>
            <div className="flex items-center gap-4">
              {collective.intelligence.leaderboard.slice(0, 3).map((l, i) => {
                const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                return (
                  <div key={i} className="flex items-center gap-1.5 flex-1">
                    <span className="text-sm font-bold font-mono" style={{ color: rankColors[i] }}>#{l.rank}</span>
                    <span className="text-[11px] text-zinc-300 truncate">{l.strategy}</span>
                    <span className="text-[10px] font-mono font-bold text-[#22C55E] flex-shrink-0">{l.delta}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-16"><p className="text-zinc-500 text-sm">Loading strategies...</p></div>
        ) : strategies.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-zinc-500 text-sm mb-3">No strategies yet. Be the first!</p>
            <button onClick={() => navigate('/')} className="px-5 py-2 rounded-xl text-sm font-medium text-white btn-glow" style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)' }}>Analyze your product</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {strategies.filter(s => filterMode === 'all' || s.mode === filterMode).map((s, i) => {
              const m = MODE_MAP[s.mode] || MODE_MAP.authority;
              const MIcon = m.icon;
              const scoreColor = s.score >= 70 ? '#22C55E' : s.score >= 40 ? '#F59E0B' : '#EF4444';
              return (
                <div key={s.id} data-testid={`gallery-card-${i}`}
                  onClick={() => setPreview(s)}
                  className="rounded-2xl p-5 cursor-pointer card-hover relative group"
                  style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MIcon className="w-4 h-4" style={{ color: m.color }} />
                      <span className="text-[10px] font-mono uppercase" style={{ color: m.color }}>{m.label}</span>
                    </div>
                    <span className="font-mono text-sm font-bold px-2.5 py-0.5 rounded-lg" style={{ color: scoreColor, background: `${scoreColor}0A`, border: `1px solid ${scoreColor}15` }}>{s.score}</span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-1 truncate">{s.product}</h3>
                  {s.category && <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md inline-block mb-2" style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.1)' }}>{s.category}</span>}
                  <p className="text-[11px] text-zinc-500 line-clamp-2 leading-relaxed">{s.title}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {s.audience && <span className="text-[9px] font-mono text-zinc-600 truncate">{s.audience}</span>}
                    {s.channel && <span className="text-[9px] font-mono text-zinc-700">via {s.channel}</span>}
                  </div>
                  <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Eye className="w-3.5 h-3.5 text-zinc-600" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 pb-16 text-center">
        <p className="text-sm text-zinc-500 mb-3">Where does your product stand?</p>
        <button onClick={() => navigate('/')} data-testid="gallery-cta"
          className="h-12 px-10 rounded-2xl text-white text-sm font-semibold btn-glow inline-flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 6px 25px rgba(124,92,255,0.25)' }}>
          Find Your Edge <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="gallery-preview-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPreview(null)} />
          <div className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden" style={{ background: '#0E0E14', border: '1px solid rgba(124,92,255,0.15)', boxShadow: '0 0 80px rgba(124,92,255,0.08)' }}>
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #7C5CFF, #FF8A3D, #7C5CFF)', opacity: 0.7 }} />
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <h2 className="text-sm font-bold text-white">{preview.product}</h2>
              <button onClick={() => setPreview(null)} className="text-zinc-500 hover:text-white p-1 rounded-lg hover:bg-[#1F1F28]"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2">
                {(() => { const m = MODE_MAP[preview.mode] || MODE_MAP.authority; const MI = m.icon; return <><MI className="w-4 h-4" style={{ color: m.color }} /><span className="text-[11px] font-mono uppercase" style={{ color: m.color }}>{m.label} Strategy</span></>; })()}
                <div className="flex-1" />
                <span className="font-mono text-lg font-bold" style={{ color: preview.score >= 70 ? '#22C55E' : '#F59E0B' }}>{preview.score}</span>
              </div>
              <h3 className="text-base font-bold text-white leading-snug">{preview.title}</h3>
              <div className="grid grid-cols-2 gap-2">
                {preview.audience && <div className="rounded-lg px-3 py-2" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}><span className="text-[9px] font-mono text-zinc-600 uppercase block">Audience</span><p className="text-[11px] text-zinc-300">{preview.audience}</p></div>}
                {preview.channel && <div className="rounded-lg px-3 py-2" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}><span className="text-[9px] font-mono text-zinc-600 uppercase block">Channel</span><p className="text-[11px] text-zinc-300">{preview.channel}</p></div>}
              </div>
              <button onClick={() => { setPreview(null); navigate(`/dashboard/${preview.id}`); }} data-testid="preview-open-dashboard"
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white btn-glow flex items-center justify-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 2px 12px rgba(124,92,255,0.25)' }}>
                View Full Strategy <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
